import { Component, computed, inject, signal } from '@angular/core';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { AnexoSic, PedidoSicStore, motivoRecusaAnexo } from '../pedido-sic.store';

@Component({
  selector: 'app-etapa-anexos-sic',
  templateUrl: './etapa-anexos-sic.component.html',
  styleUrls: ['./etapa-anexos-sic.component.css'],
  imports: [LucideModule],
})
export class EtapaAnexosSicComponent {
  protected readonly store = inject(PedidoSicStore);

  readonly arrastando = signal(false);
  readonly recusados = signal<readonly string[]>([]);

  private proximoId = 1;

  /** Os limites EM VIGOR: do departamento escolhido, e não constantes da tela. */
  readonly limites = this.store.limitesAnexo;

  readonly aceitos = computed(() => this.limites().extensoes.map((extensao) => `.${extensao}`).join(','));

  readonly formatosEmTexto = computed(() =>
    [...new Set(this.limites().extensoes.map((extensao) => (extensao === 'jpeg' ? 'jpg' : extensao)))]
      .map((extensao) => extensao.toUpperCase())
      .join(', ')
  );

  readonly tamanhoMaximoMb = computed(() => Math.round(this.limites().tamanhoBytes / (1024 * 1024)));

  /** Os erros do servidor desta etapa, mais a reavaliação depois de trocar o departamento. */
  readonly errosServidor = computed(() => this.store.errosServidor()['anexos'] ?? []);

  aoSelecionar(evento: Event): void {
    const campo = evento.target as HTMLInputElement;
    this.adicionar(campo.files);
    campo.value = '';
  }

  aoArrastar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando.set(true);
  }

  aoSairDaArea(): void {
    this.arrastando.set(false);
  }

  aoSoltar(evento: DragEvent): void {
    evento.preventDefault();
    this.arrastando.set(false);
    this.adicionar(evento.dataTransfer?.files);
  }

  remover(id: number): void {
    this.store.anexos.update((lista) => lista.filter((anexo) => anexo.id !== id));
  }

  formatarTamanho(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Aplica formato, tamanho e quantidade do departamento aqui — e guarda o `File`, que é o que
   * vai no envio. O servidor repete a checagem, inclusive o tipo real do arquivo.
   */
  private adicionar(lista: FileList | null | undefined): void {
    if (!lista?.length) return;

    const limites = this.limites();
    const recusados: string[] = [];
    const aceitos: AnexoSic[] = [];
    let disponiveis = limites.quantidade - this.store.anexos().length;

    for (const arquivo of Array.from(lista)) {
      const motivo = motivoRecusaAnexo(arquivo, limites);

      if (motivo) {
        recusados.push(motivo);
        continue;
      }

      if (disponiveis <= 0) {
        recusados.push(`${arquivo.name} — limite de ${limites.quantidade} arquivo(s) atingido.`);
        continue;
      }

      aceitos.push({ id: this.proximoId++, nome: arquivo.name, tamanho: arquivo.size, arquivo });
      disponiveis--;
    }

    this.recusados.set(recusados);

    if (aceitos.length) {
      this.store.anexos.update((atuais) => [...atuais, ...aceitos]);
    }
  }
}
